import { Component, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { configError, useAuth } from "./lib/auth.tsx";
import { AgentIndex, AgentLayout } from "./pages/AgentLayout.tsx";
import { Answers } from "./pages/Answers.tsx";
import { ConversationDetail, Conversations } from "./pages/Conversations.tsx";
import { Login, Signup } from "./pages/Gate.tsx";
import { Home } from "./pages/Home.tsx";
import { Install } from "./pages/Install.tsx";
import { Knowledge } from "./pages/Knowledge.tsx";
import { Look } from "./pages/Look.tsx";
import { Shell } from "./pages/Shell.tsx";
import { TestAgent } from "./pages/Test.tsx";

export function App() {
  const missing = configError();
  if (missing) return <Setup message={missing} />;
  return (
    <Boundary>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/app"
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Home />} />
          <Route path="agents/:agentId" element={<AgentLayout />}>
            <Route index element={<AgentIndex />} />
            <Route path="knowledge" element={<Knowledge />} />
            <Route path="answers" element={<Answers />} />
            <Route path="policy" element={<Navigate to="../answers" replace />} />
            <Route path="test" element={<TestAgent />} />
            <Route path="look" element={<Look />} />
            <Route path="install" element={<Install />} />
            <Route path="conversations" element={<Conversations />} />
            <Route path="conversations/:conversationId" element={<ConversationDetail />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </Boundary>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, session } = useAuth();
  if (!ready) return <p className="gate-loading">Checking your session…</p>;
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

function Setup({ message }: { message: string }) {
  return (
    <main className="gate">
      <section className="card setup">
        <p className="eyebrow">Setup</p>
        <h1>Connect the dashboard</h1>
        <p>{message}</p>
        <p>Copy <code>apps/dashboard/.env.example</code> to <code>apps/dashboard/.env.local</code>, then restart Vite.</p>
      </section>
    </main>
  );
}

class Boundary extends Component<{ children: ReactNode }, { message: string | null }> {
  override state = { message: null as string | null };

  static getDerivedStateFromError(error: Error): { message: string } {
    return { message: error.message || "This page failed to render." };
  }

  override render() {
    if (this.state.message) {
      return (
        <main className="gate">
          <section className="card">
            <h1>Something went wrong</h1>
            <p>{this.state.message}</p>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}
